import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, Star, Loader2, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Brand Offers Picker — tap-to-toggle grid of every product in a brand.
 * Each tile turns yellow when marked as offer (is_offer=true). Tapping
 * again removes it. Optimistic UI: the tile flips color instantly, and
 * the PATCH request runs in the background. On failure we revert.
 *
 * Requested Feb 27, 2026 — admin wants a scannable per-brand picker
 * instead of an all-or-nothing bulk toggle, so seasonal promos can be
 * curated fast per SKU.
 */
const BrandOffersPickerModal = ({ brand, onClose, onSaved }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState({}); // id → true while patch is in flight
  const [q, setQ] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/products`);
        // Filter to this brand only
        const list = (data || []).filter((p) => p.brand_id === brand.id);
        setProducts(list);
      } catch (e) {
        toast.error("فشل تحميل المنتجات");
      } finally { setLoading(false); }
    };
    load();
  }, [brand.id]);

  const toggle = async (product) => {
    if (busyIds[product.id]) return;
    const next = !product.is_offer;
    // Optimistic flip
    setProducts((prev) => prev.map((p) => p.id === product.id ? { ...p, is_offer: next, featured_offer_slot: next ? p.featured_offer_slot : null } : p));
    setBusyIds((b) => ({ ...b, [product.id]: true }));
    try {
      await axios.patch(`${API}/admin/products/${product.id}`, {
        is_offer: next,
        // Clear the featured slot when un-offering so it doesn't linger on a non-offer.
        ...(next ? {} : { featured_offer_slot: null }),
      });
    } catch (e) {
      // Revert
      setProducts((prev) => prev.map((p) => p.id === product.id ? { ...p, is_offer: !next } : p));
      toast.error(e.response?.data?.detail || "فشل الحفظ");
    } finally {
      setBusyIds((b) => { const n = { ...b }; delete n[product.id]; return n; });
    }
  };

  const filtered = useMemo(() => {
    const s = q.trim();
    if (!s) return products;
    const norm = (x) => (x || "").toLowerCase();
    return products.filter((p) =>
      norm(p.name_ar).includes(norm(s)) || norm(p.name).includes(norm(s)) ||
      norm(p.sku).includes(norm(s)) || norm(p.barcode).includes(norm(s))
    );
  }, [products, q]);

  const offerCount = products.filter((p) => p.is_offer).length;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3"
        onClick={() => { onSaved?.(); onClose(); }}
        data-testid="brand-offers-picker-overlay"
      >
        <motion.div
          initial={{ scale: 0.9, y: 30, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.9, y: 30, opacity: 0 }}
          transition={{ type: "spring", damping: 22, stiffness: 240 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="brand-offers-picker-modal"
          className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
          dir="rtl"
        >
          {/* Yellow header */}
          <div className="bg-gradient-to-l from-[#FEF3C7] via-[#FDE68A] to-[#FBBF24] px-4 py-3 border-b-2 border-[#F59E0B] relative overflow-hidden">
            <motion.div
              aria-hidden
              initial={{ x: 0, y: 0, rotate: 0 }}
              animate={{ x: [0, 8, -4, 0], y: [0, -6, 4, 0], rotate: [0, 15, -10, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-1 -right-1 text-[#B45309] pointer-events-none"
            >
              <Star className="w-10 h-10 fill-[#F59E0B]" />
            </motion.div>
            <div className="flex items-start gap-3 relative">
              <button
                onClick={() => { onSaved?.(); onClose(); }}
                data-testid="brand-offers-picker-close"
                className="bg-white/70 hover:bg-white rounded-full p-1.5 transition-colors shrink-0"
              >
                <X className="w-4 h-4 text-[#78350F]" />
              </button>
              <div className="flex-1 min-w-0">
                <h3 className="font-black text-lg text-[#78350F] flex items-center gap-2">
                  <Star className="w-5 h-5 fill-[#F59E0B] text-[#F59E0B]" />
                  اختر عروض شركة {brand.name_ar}
                </h3>
                <p className="text-[11px] text-[#78350F]/80 mt-0.5">
                  دوس على أي منتج ليصير عرض (يطلع أصفر). دوس مرة ثانية لإلغائه.
                  <span className="mx-1">•</span>
                  <span className="font-bold" dir="ltr">{offerCount}/{products.length}</span> عرض حالياً
                </p>
              </div>
            </div>
            {/* Search */}
            <div className="mt-2.5 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78350F]/60" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="ابحث عن منتج..."
                data-testid="brand-offers-picker-search"
                className="w-full pr-9 pl-4 py-2 rounded-full bg-white/80 border border-[#F59E0B]/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#F59E0B]"
              />
            </div>
          </div>

          {/* Grid */}
          <div className="flex-1 overflow-y-auto p-3 bg-[#FAFAF7]">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-[#5C6E64]">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                <span className="text-sm mr-2">جاري تحميل المنتجات...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-[#5C6E64]">
                <Search className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">لا توجد منتجات {q ? `تطابق "${q}"` : ""}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                {filtered.map((p) => {
                  const on = !!p.is_offer;
                  const busy = !!busyIds[p.id];
                  return (
                    <motion.button
                      key={p.id}
                      onClick={() => toggle(p)}
                      whileTap={{ scale: 0.96 }}
                      data-testid={`brand-offer-tile-${p.id}`}
                      data-offer-on={on ? "1" : "0"}
                      className={`relative text-right rounded-2xl p-2 border-2 transition-all overflow-hidden ${
                        on
                          ? "bg-gradient-to-br from-[#FEF3C7] to-[#FBBF24] border-[#F59E0B] shadow-md"
                          : "bg-white border-[#E4EBE6] hover:border-[#F59E0B]/50 hover:shadow"
                      }`}
                    >
                      {on && (
                        <div className="absolute top-1 left-1 bg-[#F59E0B] text-white rounded-full p-0.5 shadow-md z-10">
                          <Check className="w-3 h-3" strokeWidth={3} />
                        </div>
                      )}
                      {busy && (
                        <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] flex items-center justify-center z-20">
                          <Loader2 className="w-5 h-5 animate-spin text-[#F59E0B]" />
                        </div>
                      )}
                      <div
                        className="aspect-square rounded-xl flex items-center justify-center mb-1.5 overflow-hidden"
                        style={{ backgroundColor: p.color || "#F0F9FF" }}
                      >
                        {p.image_path ? (
                          <img src={`${API}/files/${p.image_path}`} alt={p.name_ar} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-[10px] font-bold text-center px-1 text-[#1E3A5F]">
                            {(p.name_ar || "").slice(0, 12)}
                          </span>
                        )}
                      </div>
                      <p className={`font-bold text-xs leading-tight truncate ${on ? "text-[#78350F]" : "text-[#1E3A5F]"}`}>
                        {p.name_ar}
                      </p>
                      <p className={`text-[11px] font-black mt-0.5 ${on ? "text-[#78350F]" : "text-[#2D4A3E]"}`} dir="ltr">
                        {Number(p.price || 0).toLocaleString("en-US")} د.ع
                      </p>
                    </motion.button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t-2 border-[#F59E0B]/30 bg-white px-4 py-2.5 flex items-center justify-between gap-2">
            <p className="text-[11px] text-[#5C6E64]">
              التعديلات تتحفظ تلقائياً — دوس ✕ لما تخلص
            </p>
            <button
              onClick={() => { onSaved?.(); onClose(); }}
              data-testid="brand-offers-picker-done"
              className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-black text-sm px-5 py-2 rounded-full shadow-md active:scale-95 transition-all"
            >
              تم
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BrandOffersPickerModal;
